import { useState, useRef } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import FunProgress from '../ui/FunProgress';
import { Mic, Loader2, Copy, Download, Upload, FileAudio, FileVideo, Languages, ArrowRight, RotateCcw, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HistorySection from '../history/HistorySection';

// ── Language lists ───────────────────────────────────────────────────────────

const TRANSCRIBE_LANGUAGES = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en',   label: 'English' },
  { value: 'hi',   label: 'Hindi (हिन्दी)' },
  { value: 'kn',   label: 'Kannada (ಕನ್ನಡ)' },
  { value: 'ta',   label: 'Tamil (தமிழ்)' },
  { value: 'te',   label: 'Telugu (తెలుగు)' },
  { value: 'ml',   label: 'Malayalam (മലയാളം)' },
  { value: 'mr',   label: 'Marathi (मराठी)' },
  { value: 'bn',   label: 'Bengali (বাংলা)' },
  { value: 'gu',   label: 'Gujarati (ગુજરાતી)' },
  { value: 'pa',   label: 'Punjabi (ਪੰਜਾਬੀ)' },
  { value: 'ur',   label: 'Urdu (اردو)' },
  { value: 'es',   label: 'Spanish' },
  { value: 'fr',   label: 'French' },
  { value: 'de',   label: 'German' },
  { value: 'ja',   label: 'Japanese' },
  { value: 'zh',   label: 'Chinese' },
  { value: 'ar',   label: 'Arabic' },
  { value: 'pt',   label: 'Portuguese' },
  { value: 'ru',   label: 'Russian' },
  { value: 'ko',   label: 'Korean' },
  { value: 'it',   label: 'Italian' },
];

// Translation target languages — includes code-mixed varieties
const TRANSLATE_LANGUAGES = [
  // Standard
  { value: 'English',    label: 'English' },
  { value: 'Hindi',      label: 'Hindi (हिन्दी)' },
  { value: 'Kannada',    label: 'Kannada (ಕನ್ನಡ)' },
  { value: 'Tamil',      label: 'Tamil (தமிழ்)' },
  { value: 'Telugu',     label: 'Telugu (తెలుగు)' },
  { value: 'Malayalam',  label: 'Malayalam (മലയാളം)' },
  { value: 'Marathi',    label: 'Marathi (मराठी)' },
  { value: 'Bengali',    label: 'Bengali (বাংলা)' },
  { value: 'Gujarati',   label: 'Gujarati (ગુજરાતી)' },
  { value: 'Punjabi',    label: 'Punjabi (ਪੰਜਾਬੀ)' },
  { value: 'Urdu',       label: 'Urdu (اردو)' },
  { value: 'Odia',       label: 'Odia (ଓଡ଼ିଆ)' },
  { value: 'Assamese',   label: 'Assamese (অসমীয়া)' },
  { value: 'Sanskrit',   label: 'Sanskrit (संस्कृतम्)' },
  { value: 'Spanish',    label: 'Spanish' },
  { value: 'French',     label: 'French' },
  { value: 'German',     label: 'German' },
  { value: 'Arabic',     label: 'Arabic' },
  { value: 'Portuguese', label: 'Portuguese' },
  { value: 'Russian',    label: 'Russian' },
  { value: 'Japanese',   label: 'Japanese' },
  { value: 'Chinese',    label: 'Chinese (Simplified)' },
  { value: 'Korean',     label: 'Korean' },
  { value: 'Italian',    label: 'Italian' },
  { value: 'Turkish',    label: 'Turkish' },
  { value: 'Dutch',      label: 'Dutch' },
  { value: 'Swahili',    label: 'Swahili' },
  // Code-mixed / colloquial
  { value: 'kanglish',   label: 'Kanglish (Kannada + English)' },
  { value: 'hinglish',   label: 'Hinglish (Hindi + English)' },
  { value: 'tanglish',   label: 'Tanglish (Tamil + English)' },
  { value: 'manglish',   label: 'Manglish (Malayalam + English)' },
  { value: 'tenglish',   label: 'Tenglish (Telugu + English)' },
  { value: 'benglish',   label: 'Benglish (Bengali + English)' },
];

const OUTPUT_FORMATS = [
  { value: 'text', label: 'Plain Text' },
  { value: 'srt',  label: 'SRT Subtitles (.srt)' },
  { value: 'vtt',  label: 'WebVTT (.vtt)' },
];

const ACCEPTED_TYPES = [
  'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/webm', 'audio/ogg',
  'audio/flac', 'audio/aac', 'audio/x-m4a',
  'video/mp4', 'video/webm', 'video/mpeg', 'video/quicktime',
  'video/x-msvideo', 'video/x-matroska',
];

const ACCEPTED_EXT = '.mp3,.mp4,.wav,.webm,.ogg,.flac,.aac,.m4a,.mov,.avi,.mkv';

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Component ────────────────────────────────────────────────────────────────

const AITranscribe = () => {
  // Transcription state
  const [file, setFile]                       = useState<File | null>(null);
  const [language, setLanguage]               = useState('auto');
  const [outputFormat, setOutputFormat]       = useState('text');
  const [transcribing, setTranscribing]       = useState(false);
  const [transcript, setTranscript]           = useState('');
  const [detectedLang, setDetectedLang]       = useState('');
  const [duration, setDuration]               = useState<number | null>(null);
  const [error, setError]                     = useState<string | null>(null);
  const [dragOver, setDragOver]               = useState(false);
  const [progress, setProgress]               = useState(0);
  const [progStatus, setProgStatus]           = useState('');

  // Translation state
  const [targetLang, setTargetLang]           = useState('English');
  const [translating, setTranslating]         = useState(false);
  const [translated, setTranslated]           = useState('');
  const [translateError, setTranslateError]   = useState<string | null>(null);

  // Copy states
  const [copiedOriginal, setCopiedOriginal]   = useState(false);
  const [copiedTranslated, setCopiedTranslated] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isVideo = file && file.type.startsWith('video/');

  const handleFile = (f: File) => {
    if (!ACCEPTED_TYPES.includes(f.type) && !f.name.match(/\.(mp3|mp4|wav|webm|ogg|flac|aac|m4a|mov|avi|mkv)$/i)) {
      setError('Unsupported file type. Please upload an audio or video file.');
      return;
    }
    if (f.size > 200 * 1024 * 1024) { setError('File too large — max 200 MB.'); return; }
    setFile(f);
    setTranscript('');
    setTranslated('');
    setError(null);
    setDetectedLang('');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleTranscribe = async () => {
    if (!file) return;
    setTranscribing(true);
    setError(null);
    setTranscript('');
    setTranslated('');
    setDetectedLang('');
    setProgress(0);
    setProgStatus('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language);
      formData.append('outputFormat', outputFormat);

      // Simulate progress since transcription doesn't stream progress
      setProgress(10); setProgStatus('Uploading file...');

      // Upload with XHR to track upload progress. xhr.onload only fires once
      // the FULL response (i.e. the finished transcript) has arrived, so any
      // "simulate transcribing progress" step must run *while this is in
      // flight*, not after awaiting it — otherwise the bar just freezes at
      // the upload's final percentage for the whole transcription time.
      const xhrPromise = new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/transcribe');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 40);
            setProgress(10 + pct);
            setProgStatus(`Uploading... ${(e.loaded / 1024 / 1024).toFixed(1)} MB / ${(e.total / 1024 / 1024).toFixed(1)} MB`);
          }
        };
        xhr.upload.onloadend = () => {
          setProgress(50);
          setProgStatus('Transcribing with AI...');
        };
        xhr.onload = () => {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid response')); }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(formData);
      });

      // Keep the bar visibly moving from 50 → 90 while the server transcribes.
      const interval = setInterval(() => {
        setProgress(p => (p < 90 ? p + 1 : p));
      }, 800);

      let uploadResult: any;
      try {
        uploadResult = await xhrPromise;
      } finally {
        clearInterval(interval);
      }

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Transcription failed');
      }

      setProgress(100); setProgStatus('Done!');
      setTranscript(uploadResult.text);
      if (uploadResult.language) setDetectedLang(uploadResult.language);
      if (uploadResult.duration) setDuration(Math.round(uploadResult.duration));
      // Let the 100% state actually paint before the bar unmounts — otherwise
      // it and the "transcribing: false" flip land in the same React batch
      // and the bar visually jumps from ~90% straight to gone.
      await new Promise((r) => setTimeout(r, 400));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTranscribing(false);
    }
  };

  const handleTranslate = async () => {
    if (!transcript) return;
    setTranslating(true);
    setTranslateError(null);
    setTranslated('');

    try {
      const res = await fetch('/api/transcribe/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transcript, targetLanguage: targetLang, sourceLanguage: detectedLang }),
      });

      if (!res.ok) { throw new Error('Translation failed'); }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      // SSE events can arrive split across multiple network reads — a long
      // translation is one large "data: {...}" line that's very likely to
      // get cut in half by the browser's stream chunking. Parsing per-read
      // instead of buffering across reads meant a cut JSON line threw,
      // silently discarded by the empty catch, and nothing ever rendered.
      let buffer = '';

      while (true) {
        const { done, value } = await reader!.read();
        if (value) buffer += decoder.decode(value, { stream: true });

        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          for (const line of rawEvent.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            try {
              const d = JSON.parse(line.slice(6));
              if (d.type === 'chunk') { acc += d.content; setTranslated(acc); }
              else if (d.type === 'complete') { setTranslated(d.content || acc); }
              else if (d.type === 'error') { setTranslateError(d.error); }
            } catch {}
          }
        }

        if (done) break;
      }
    } catch (err: any) {
      setTranslateError(err.message);
    } finally {
      setTranslating(false);
    }
  };

  const copyText = (text: string, which: 'original' | 'translated') => {
    navigator.clipboard.writeText(text).then(() => {
      if (which === 'original') { setCopiedOriginal(true); setTimeout(() => setCopiedOriginal(false), 2000); }
      else { setCopiedTranslated(true); setTimeout(() => setCopiedTranslated(false), 2000); }
    });
  };

  const downloadText = (text: string, suffix: string) => {
    const ext = outputFormat === 'srt' ? 'srt' : outputFormat === 'vtt' ? 'vtt' : 'txt';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${suffix}_${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const targetLabel = TRANSLATE_LANGUAGES.find((l) => l.value === targetLang)?.label || targetLang;

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#54c4b6] to-[#216974] rounded-2xl shadow-lg shadow-teal-200">
          <Mic size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">AI Transcribe & Translate</h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto font-medium">
          Transcribe any audio or video, then instantly translate to any language — including Kanglish, Hinglish, and more.
        </p>
      </div>

      {/* Upload + Settings */}
      <Card className="overflow-hidden border-0 shadow-2xl shadow-teal-100">
        <div className="p-8 space-y-6">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${
              dragOver ? 'border-teal-500 bg-[#54c4b6]' :
              file ? 'border-teal-400 bg-[#54c4b6]' :
              'border-gray-300 bg-gray-50 hover:border-teal-400 hover:bg-[#54c4b6]/40'
            }`}
          >
            <input ref={fileInputRef} type="file" accept={ACCEPTED_EXT} className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            {file ? (
              <>
                <div className="w-12 h-12 rounded-xl bg-[#54c4b6] flex items-center justify-center">
                  {isVideo ? <FileVideo size={24} className="text-[#54c4b6]" /> : <FileAudio size={24} className="text-[#54c4b6]" />}
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-800 text-sm">{file.name}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{formatFileSize(file.size)} · {isVideo ? 'Video' : 'Audio'}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setFile(null); setTranscript(''); setTranslated(''); setError(null); }}
                  className="text-xs text-[#216974] hover:text-[#216974] font-medium">
                  Remove file
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-gray-200 flex items-center justify-center">
                  <Upload size={24} className="text-gray-400" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-700">Drop your file here or click to browse</p>
                  <p className="text-gray-400 text-sm mt-1">MP3, MP4, WAV, M4A, MOV, AVI, MKV, WEBM · Max 200 MB</p>
                </div>
              </>
            )}
          </div>

          {/* Settings row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                <Languages size={14} className="inline mr-1" />Audio Language
              </label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                {TRANSCRIBE_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Output Format</label>
              <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                {OUTPUT_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-semibold flex items-center gap-2">
                <span>⚠️</span> {error}
              </motion.div>
            )}
          </AnimatePresence>

          <Button onClick={handleTranscribe} disabled={!file || transcribing} isLoading={transcribing}
            className="w-full h-12 bg-gradient-to-r from-[#54c4b6] to-[#216974] hover:scale-105 transition-transform text-white font-bold text-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            leftIcon={transcribing ? <Loader2 className="animate-spin" size={20} /> : <Mic size={20} />}>
            {transcribing ? 'Transcribing…' : 'Transcribe'}
          </Button>
          <AnimatePresence>
            {transcribing && progress > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="glass-card rounded-2xl p-4">
                <FunProgress percent={progress} tool="transcribe" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>

      {/* Results: side-by-side transcript + translation */}
      <AnimatePresence>
        {transcript && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            className="space-y-6">

            {/* Meta row */}
            <div className="flex flex-wrap gap-2 items-center">
              {detectedLang && (
                <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 border border-teal-200 px-3 py-1 rounded-full">
                  Detected: {detectedLang}
                </span>
              )}
              {duration !== null && (
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  Duration: {duration}s
                </span>
              )}
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {transcript.split(/\s+/).filter(Boolean).length} words
              </span>
              <button onClick={() => { setTranscript(''); setTranslated(''); setFile(null); }}
                className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 font-medium">
                <RotateCcw size={12} /> Start over
              </button>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Left: Original transcript */}
              <Card className="border border-gray-200 shadow-md overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/60">
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Original Transcript</p>
                    <p className="text-sm font-semibold text-gray-700 mt-0.5 capitalize">{detectedLang || language}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => copyText(transcript, 'original')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                      {copiedOriginal ? <Check size={12} className="text-[#54c4b6]" /> : <Copy size={12} />}
                      {copiedOriginal ? 'Copied' : 'Copy'}
                    </button>
                    <button onClick={() => downloadText(transcript, 'transcript')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                      <Download size={12} /> Download
                    </button>
                  </div>
                </div>
                <div className="p-5 max-h-[400px] overflow-y-auto">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">{transcript}</pre>
                </div>
              </Card>

              {/* Right: Translation */}
              <Card className="border border-gray-200 shadow-md overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-[#216974]/50">
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Translate to</p>
                    <select value={targetLang} onChange={(e) => { setTargetLang(e.target.value); setTranslated(''); }}
                      className="w-full text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent">
                      {TRANSLATE_LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {translated && (
                      <>
                        <button onClick={() => copyText(translated, 'translated')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                          {copiedTranslated ? <Check size={12} className="text-[#54c4b6]" /> : <Copy size={12} />}
                          {copiedTranslated ? 'Copied' : 'Copy'}
                        </button>
                        <button onClick={() => downloadText(translated, `translated_${targetLang}`)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                          <Download size={12} /> Download
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Translate button */}
                <div className="px-5 pt-4">
                  <Button onClick={handleTranslate} disabled={translating}
                    className="w-full h-10 bg-gradient-to-r from-[#216974] to-[#54c4b6] hover:opacity-90 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    leftIcon={translating ? <Loader2 className="animate-spin" size={16} /> : <ArrowRight size={16} />}>
                    {translating ? `Translating to ${targetLabel}…` : `Translate to ${targetLabel}`}
                  </Button>
                </div>

                <div className="p-5 max-h-[340px] overflow-y-auto">
                  <AnimatePresence>
                    {translateError && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-[#216974] font-medium">
                        ⚠️ {translateError}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  {translated ? (
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                      {translated}
                      {translating && <span className="inline-block w-2 h-4 bg-[#216974] animate-pulse ml-0.5 align-middle" />}
                    </pre>
                  ) : !translateError ? (
                    <div className="flex flex-col items-center justify-center h-32 text-center">
                      <Languages size={28} className="text-gray-200 mb-2" />
                      <p className="text-sm text-gray-400">
                        {translating ? 'Translating…' : `Click to translate into ${targetLabel}`}
                      </p>
                      {!translating && (
                        <p className="text-xs text-gray-300 mt-1">
                          Change the language above and translate as many times as you want
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              </Card>
            </div>

            {/* Language quick-switch chips */}
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Quick Translate</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'English', label: 'English' },
                  { value: 'Hindi', label: 'Hindi' },
                  { value: 'Kannada', label: 'Kannada' },
                  { value: 'Tamil', label: 'Tamil' },
                  { value: 'Telugu', label: 'Telugu' },
                  { value: 'Malayalam', label: 'Malayalam' },
                  { value: 'kanglish', label: 'Kanglish' },
                  { value: 'hinglish', label: 'Hinglish' },
                  { value: 'tanglish', label: 'Tanglish' },
                  { value: 'manglish', label: 'Manglish' },
                  { value: 'Bengali', label: 'Bengali' },
                  { value: 'Marathi', label: 'Marathi' },
                  { value: 'Gujarati', label: 'Gujarati' },
                  { value: 'Punjabi', label: 'Punjabi' },
                  { value: 'Urdu', label: 'Urdu' },
                  { value: 'Spanish', label: 'Spanish' },
                  { value: 'French', label: 'French' },
                  { value: 'Arabic', label: 'Arabic' },
                  { value: 'Japanese', label: 'Japanese' },
                  { value: 'Chinese', label: 'Chinese' },
                ].map((chip) => (
                  <button
                    key={chip.value}
                    onClick={() => {
                      setTargetLang(chip.value);
                      setTranslated('');
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      targetLang === chip.value
                        ? 'bg-[#216974] text-white border-[#54c4b6]'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#54c4b6] hover:text-[#216974]'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <HistorySection toolId="ai-transcribe" />
    </div>
  );
};

export default AITranscribe;
